<template>
  <component
    :is="props.useClientLayout ? AdminLayout : PortalLayout"
    section="Reports"
    title="VR Report"
    hideHeader
  >
    <div class="pb-4">
      <BackButton to=".."> Back to Reports List </BackButton>
    </div>
    <div v-if="isPropertyLoading"><Loading /></div>
    <div v-else>
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl">
          <div class="font-bold">VR Report</div>
          <div>
            {{ user.isTenantAdmin ? user.tenantName : property?.name }}
          </div>
        </div>
      </h1>

      <Loading v-if="isReportLoading" />
      <div v-else class="report-container mt-4">
        <div class="flex">
          <div class="flex flex-row gap-2 grow">
            <div v-if="!user.isTenantAdmin" class="basis-1/5">
              <BaseSelect
                defaultText="All Tenants"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredTenant"
                :options="tenants"
              />
            </div>
            <div class="basis-1/5">
              <BaseSelect
                defaultText="All Roles"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredRole"
                :options="siteRoles"
              />
            </div>
            <div v-if="!props.useClientLayout" class="basis-1/5">
              <BaseCheckbox
                label="Exclude Test Accounts"
                v-model="excludeTestAccounts"
                class="my-4"
              />
            </div>
          </div>
          <BaseButton class="float-right my-1 text-sm ml-2" @click="downloadExcel">
            Export to Excel
          </BaseButton>
        </div>
        <table class="table-auto w-full mt-4 border-2 shadow text-sm">
          <thead class="font-sm font-bold">
            <tr>
              <td class="pl-3"></td>
              <td class="pl-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.FirstName)">
                First Name
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.FirstName"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="pl-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.LastName)">
                Last Name
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.LastName"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="pl-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.Email)">
                Email
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Email"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredTenant == null || filteredTenant.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Tenant)"
              >
                Tenant
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Tenant"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredRole == null || filteredRole.length == 0"
                class="pl-3 py-2 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Role)"
              >
                Role
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Role"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="px-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.Date)">
                First Accomplishment Date
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Date"
                  :isUp="!isSortingDown"
                />
              </td>
            </tr>
          </thead>
          <template v-for="(result, i) in filteredvrResultsByUser" :key="i">
            <tr
              class="font-xs font-mono border-bottom-2 hover:border-sky-800 hover:bg-sky-300 cursor-pointer"
              :class="i % 2 ? '' : 'bg-stone-200'"
              @click="selectClick(result)"
            >
              <td class="pl-3">
                <div class="px-2">
                  <IHeroiconsOutlineChevronUp
                    class="mx-auto"
                    v-if="result.loginEmail === selectedEmail"
                  />
                  <IHeroiconsOutlineChevronDown class="mx-auto" v-else />
                </div>
              </td>
              <td class="pl-3">{{ result.firstName }}</td>
              <td class="pl-3">{{ result.lastName }}</td>
              <td class="pl-3">{{ result.loginEmail }}</td>
              <td v-if="filteredTenant == null || filteredTenant.length == 0" class="pl-3">
                {{ result.tenant }}
              </td>
              <td v-if="filteredRole == null || filteredRole.length == 0" class="pl-3">
                {{ result.role }}
                <span v-if="getSiteRoleByName(result.role)?.isStaffRole" class="text-gray-500">
                  (Staff)
                </span>
              </td>
              <td class="px-3">{{ formatDateTime(result.dateAccomplished) }}</td>
            </tr>
            <template v-if="result.loginEmail === selectedEmail">
              <tr>
                <td></td>
                <td :colspan="6">
                  <table class="m-4 table-auto border-2 shadow">
                    <thead class="font-bold">
                      <tr>
                        <td
                          class="pl-8 cursor-pointer"
                          @click="ChangeDetailsSort(DetailsColumnsToSort.Accomplishment)"
                        >
                          Accomplishment
                          <UpDownArrow
                            class="float-right"
                            v-if="detailsColumnToSort == DetailsColumnsToSort.Accomplishment"
                            :isUp="!isSortingDetailsDown"
                          />
                        </td>
                        <td
                          class="px-8 py-2 cursor-pointer"
                          @click="ChangeDetailsSort(DetailsColumnsToSort.Date)"
                        >
                          Date
                          <UpDownArrow
                            class="float-right"
                            v-if="detailsColumnToSort == DetailsColumnsToSort.Date"
                            :isUp="!isSortingDetailsDown"
                          />
                        </td>
                      </tr>
                    </thead>
                    <tr
                      v-for="(result, i) in sortedDetailsvrResults.filter(
                        (r) => r.loginEmail === selectedEmail
                      )"
                      :key="i"
                      class="border-bottom-2"
                      :class="i % 2 ? '' : 'bg-stone-200'"
                    >
                      <td class="pl-8">
                        {{ result.accomplishment }}
                      </td>
                      <td class="px-8">
                        {{ formatDateTime(result.dateAccomplished) }}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </template>
          </template>
        </table>
        <div>
          <BaseButton class="float-right my-1 text-sm ml-2" @click="downloadExcel">
            Export to Excel
          </BaseButton>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { IContent } from "json-as-xlsx";
import * as jsonAsXlsx from "json-as-xlsx";
import PortalLayout from "../../layouts/PortalLayout.vue";
import AdminLayout from "../../layouts/AdminLayout.vue";
import { useBldgStore } from "../../store/bldgStore";
import { VRAccomplishmentDTO } from "../../store/generatedFetchClient";
import { useReportStore } from "../../store/reportStore";
import { NameValue } from "../../sharedTypes";
import { useUserStore } from "../../store/userStore";

const props = withDefaults(defineProps<{ useClientLayout?: boolean }>(), {
  useClientLayout: false,
});

const bldgStore = useBldgStore();
const userStore = useUserStore();
const reportStore = useReportStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var user = computed(() => userStore.user);
var tenants = computed<string[]>(() => property.value.tenants.map((t) => t.name));
var siteRoles = computed<NameValue[]>(() =>
  property.value.siteRoles.map(
    (sr) =>
      ({
        name: sr.roleAlias + (sr.isStaffRole ? " (Staff)" : ""),
        value: sr.siteRoleId,
      } as NameValue)
  )
);

var isReportLoading = ref(false);
var vrResultsAll = computed<VRAccomplishmentDTO[]>(() => reportStore.vrResults);
var vrResultsByUser = computed<VRAccomplishmentDTO[]>(() => {
  let resultsByUser = [] as VRAccomplishmentDTO[];

  vrResultsAll.value.forEach((r) => {
    if (!resultsByUser.find((existing) => existing.loginEmail === r.loginEmail))
      resultsByUser.push(r);
  });

  return resultsByUser;
});

var filteredvrResultsByUser = computed<VRAccomplishmentDTO[]>(() => {
  let results = vrResultsByUser.value.slice();
  if (filteredTenant.value != null && filteredTenant.value.length > 0)
    results = results.filter((result) => result.tenant == filteredTenant.value);
  if (filteredRole.value != null && filteredRole.value.length > 0)
    results = results.filter(
      (result) => result.role == getSiteRoleById(parseInt(filteredRole.value))?.roleAlias
    );

  switch (columnToSort.value) {
    case ColumnsToSort.FirstName:
      results = results.sort((a, b) => {
        if (a.firstName == b.firstName) {
          return (a.lastName ?? "").localeCompare(b.lastName ?? "");
        }
        return (a.firstName ?? "").localeCompare(b.firstName ?? "");
      });
      break;
    case ColumnsToSort.LastName:
      results = results.sort((a, b) => {
        if (a.lastName == b.lastName) {
          return (a.firstName ?? "").localeCompare(b.firstName ?? "");
        }
        return (a.lastName ?? "").localeCompare(b.lastName ?? "");
      });
      break;
    case ColumnsToSort.Email:
      results = results.sort((a, b) => (a.loginEmail ?? "").localeCompare(b.loginEmail ?? ""));
      break;
    case ColumnsToSort.Tenant:
      results = results.sort((a, b) => (a.tenant ?? "").localeCompare(b.tenant ?? ""));
      break;
    case ColumnsToSort.Role:
      results = results.sort((a, b) => (a.role ?? "").localeCompare(b.role ?? ""));
      break;
    case ColumnsToSort.Date:
      results.sort(
        (a, b) => new Date(a.dateAccomplished).getTime() - new Date(b.dateAccomplished).getTime()
      );
      break;
  }

  if (isSortingDown.value) {
    results.reverse();
  }

  return results;
});

//Filters
var filteredTenant = ref<string>("");
var filteredRole = ref<string>("");

let excludeTestAccounts = ref(false);

enum ColumnsToSort {
  FirstName,
  LastName,
  Email,
  Tenant,
  Role,
  Date,
}

var columnToSort = ref<ColumnsToSort>(ColumnsToSort.FirstName);
var isSortingDown = ref<boolean>(true);

function ChangeSort(column: ColumnsToSort) {
  if (column == columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = true;
  }
}

//For the details sub-table
enum DetailsColumnsToSort {
  Accomplishment,
  Date,
}

var detailsColumnToSort = ref<DetailsColumnsToSort>(DetailsColumnsToSort.Accomplishment);
var isSortingDetailsDown = ref<boolean>(true);

function ChangeDetailsSort(column: DetailsColumnsToSort) {
  if (column == detailsColumnToSort.value) {
    isSortingDetailsDown.value = !isSortingDetailsDown.value;
  } else {
    detailsColumnToSort.value = column;
    isSortingDetailsDown.value = true;
  }
}

var sortedDetailsvrResults = computed<VRAccomplishmentDTO[]>(() => {
  let results = Object.assign([], vrResultsAll.value);

  switch (detailsColumnToSort.value) {
    case DetailsColumnsToSort.Accomplishment:
      results.sort((a, b) => (a.accomplishment > b.accomplishment ? -1 : 1));
      break;
    case DetailsColumnsToSort.Date:
      results.sort(
        (a, b) => new Date(a.dateAccomplished).getTime() - new Date(b.dateAccomplished).getTime()
      );
      break;
  }

  if (isSortingDetailsDown.value) {
    results.reverse();
  }

  return results;
});

onMounted(loadData);
watch(property, loadData);
watch(excludeTestAccounts, loadData);

async function loadData() {
  isReportLoading.value = true;
  if (property?.value?.portalId) {
    if (props.useClientLayout) excludeTestAccounts.value = true;
    await reportStore.getVRReport(property.value.portalId, excludeTestAccounts.value);
  }

  if (user.value.isTenantAdmin) {
    filteredTenant.value = user.value.tenantName;
  }

  isReportLoading.value = false;
}

let selectedEmail = ref("");
function selectClick(result: VRAccomplishmentDTO) {
  // Same item clicked twice => Toggle selection
  selectedEmail.value = result.loginEmail === selectedEmail.value ? "" : result.loginEmail;
}

function formatDateTime(date: any) {
  let d = new Date(date);
  let formatedDate = d.toDateString() + ", " + d.toLocaleTimeString();
  return formatedDate === "Invalid Date" ? "" : formatedDate;
}

function downloadExcel() {
  // Extract the xlsx function from the namespace import
  const xlsx = (jsonAsXlsx as any).default || (jsonAsXlsx as any).xlsx || jsonAsXlsx;

  let data = [
    {
      sheet: "Accomplishments",
      columns: [
        { label: "First Name", value: "firstname" },
        { label: "Last Name", value: "lastname" },
        { label: "Email", value: "email" },
        { label: "Tenant", value: "tenant" },
        { label: "Role", value: "role" },
        { label: "Accomplishment", value: "accomplishment" },
        { label: "Date", value: "date" },
      ],
      content: [] as IContent[],
    },
  ];

  vrResultsAll.value.map((accomplishment) => {
    data[0].content.push({
      firstname: accomplishment.firstName,
      lastname: accomplishment.lastName,
      email: accomplishment.loginEmail,
      tenant: accomplishment.tenant,
      role: accomplishment.role,
      accomplishment: accomplishment.accomplishment,
      date: new Date(accomplishment.dateAccomplished).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }),
    });
  });

  xlsx(data, {
    fileName:
      "VR_Accomplishments_" +
      property.value.name.replace(" ", "_") +
      "_" +
      new Date(Date.now()).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }),
  });
}

// #region Helpers

var getSiteRoleById = (siteRoleId: number) =>
  property.value?.siteRoles?.find((r) => r.siteRoleId === siteRoleId);

var getSiteRoleByName = (roleName: string) =>
  property.value?.siteRoles?.find((r) => r.roleAlias === roleName);

// #endregion Helpers
</script>
